import React, {createContext, useContext, useState, useEffect} from "react";

import {APIContext} from "./APIContext";
import {AzureAuthContext} from "./AzureAuthContext";
import {ConfigContext} from "./ConfigContext";

export const UserContext = createContext({});

export const UserContextProvider = ({children}) => {
    const {searchFeature, editFeature, reportFeature, userAdminFeature, userActivityLogFeature} =
        useContext(ConfigContext);
    const [authorizationData, setAuthorizationData] = useState({});
    const [isAuthorized, setIsAuthorized] = useState(false);
    const [isAuthorizationReady, setIsAuthorizationReady] = useState(false);
    const {isUserProfileDone, userProfile} = useContext(AzureAuthContext);
    const {ROLES, getUser} = useContext(APIContext);
    const FULL_ACCESS = [ROLES.SUPPORT, ROLES.ADMIN, ROLES.COMPLIANCE, ROLES.VIEW, ROLES.PMS, ROLES.US_SUPPORT]

    //MODULES
    const SEARCH_MODULE = [ROLES.VIEW, ROLES.US_APPROVAL, ROLES.US_MARKETING, ROLES.PMS, ROLES.SUPPORT, ROLES.US_SUPPORT, ROLES.ADMIN];
    const SETTINGS_MODULE = [ROLES.ADMIN];
    const US_APPROVAL_FEATURES = [ROLES.US_APPROVAL, ROLES.US_MARKETING, ROLES.US_SUPPORT, ROLES.ADMIN];
    const REBATE_PROGRAM = [ROLES.ADMIN, ROLES.US_MARKETING]
    const MAINTENANCE_MODULE = [ROLES.ADMIN]

    //REPORTS
    const DISTRIBUTOR_REPORT = [ROLES.VIEW, ROLES.US_APPROVAL, ROLES.US_MARKETING, ROLES.SUPPORT, ROLES.US_SUPPORT, ROLES.ADMIN];
    const DT_REPORT = [ROLES.PMS, ROLES.US_MARKETING, ROLES.US_SUPPORT, ROLES.ADMIN];
    const BIANNUAL_AND_EXPLANT_INVESTIGATION = [ROLES.PMS, ROLES.ADMIN];
    const CUSTOMER_CARE_REPORT = [ROLES.VIEW, ROLES.US_APPROVAL, ROLES.US_MARKETING, ROLES.SUPPORT, ROLES.US_SUPPORT, ROLES.ADMIN]

    //EDIT INFO
    const EDIT_PATIENT_INFO = [ROLES.SUPPORT, ROLES.US_SUPPORT, ROLES.ADMIN];
    const EDTI_SURGERY_INFO = [ROLES.SUPPORT, ROLES.US_SUPPORT, ROLES.ADMIN];
    const EDIT_IMPLANT = [ROLES.PMS, ROLES.SUPPORT, ROLES.US_SUPPORT, ROLES.ADMIN];
    const EDIT_TRACKING_STATUS = [ROLES.PMS, ROLES.US_MARKETING, ROLES.SUPPORT, ROLES.ADMIN];
    const EDIT_WARRANTY = [ROLES.ADMIN];
    const EDIT_US_APPROVAL = [ROLES.US_APPROVAL, ROLES.US_MARKETING, ROLES.US_SUPPORT, ROLES.ADMIN];

    //CREATE INFO
    const CREATE_US_APPROVAL = [ROLES.US_APPROVAL, ROLES.US_MARKETING, ROLES.US_SUPPORT, ROLES.ADMIN];

    const readerRoles = searchFeature
        ? FULL_ACCESS
        : [];

    const editorRoles = editFeature ? [ROLES.ADMIN, ROLES.SUPPORT] : [];

    const reportRoles = reportFeature
        ? FULL_ACCESS
        : [];

    const userAdminRoles = userAdminFeature ? [ROLES.ADMIN] : [];

    const hasRoleIn = (roles) => {
        if(!authorizationData.isActive) return false;
        const userRole = authorizationData?.role
        if(!userRole || !userRole.active) return false;
        return roles.includes(userRole.name);
    }

    const canAccessSearchModule = () => hasRoleIn( SEARCH_MODULE );
    const canEditPatient = () => hasRoleIn(EDIT_PATIENT_INFO);
    const canEditSurgery = () => hasRoleIn(EDTI_SURGERY_INFO);
    const canEditImplant = () => hasRoleIn(EDIT_IMPLANT);
    const canEditImplantTrackingStatus = () => hasRoleIn(EDIT_TRACKING_STATUS);
    const canEditImplantWarranty = () => hasRoleIn(EDIT_WARRANTY);
    const canEditUSApproval = () => hasRoleIn(EDIT_US_APPROVAL);
        //[ROLES.ADMIN, ROLES.SUPPORT]);
    const canAccessMaintenanceModule = () => hasRoleIn(MAINTENANCE_MODULE);

    const canCreateUSApproval = () => hasRoleIn(CREATE_US_APPROVAL);

    const canAccessReportModule = () => hasRoleIn([...reportRoles, ROLES.PMS, ROLES.US_APPROVAL, ROLES.US_MARKETING]);

    const canAccessCustomerCareReport = () => hasRoleIn(CUSTOMER_CARE_REPORT);
    const canAccessDistributorReport = () => hasRoleIn(DISTRIBUTOR_REPORT);
    const canAccessImplantReport = () => hasRoleIn([reportRoles, ROLES.US_APPROVAL, ROLES.US_MARKETING]);
    const canAccessDeviceTrackingPMS = () => hasRoleIn(DT_REPORT);
    const canAccessExplantInvestigationPMS = () => hasRoleIn(BIANNUAL_AND_EXPLANT_INVESTIGATION);

    const canAccessUserModule = () => hasRoleIn(userAdminRoles);
    const canAddUser = () => hasRoleIn(userAdminRoles);
    const canEditUser = () => hasRoleIn(userAdminRoles);
    const canViewUserActivityLog = () =>
        userActivityLogFeature && hasRoleIn(userAdminRoles);

    const canAccessSettingModule = () => hasRoleIn(SETTINGS_MODULE);

    const canAccessUSASupportFeatures = ()=> hasRoleIn(US_APPROVAL_FEATURES);

    const canAccessRebateProgram = ()=> hasRoleIn(REBATE_PROGRAM);

    useEffect(() => {
        if (isUserProfileDone)
            getUser(userProfile?.mail).then((data) => {
                setAuthorizationData(data?.user);
                setIsAuthorizationReady(true);
            });
    }, [isUserProfileDone, userProfile]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setIsAuthorized(hasRoleIn(Object.values(ROLES)));
    }, [authorizationData]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <UserContext.Provider
            value={{
                isAuthorized,
                isAuthorizationReady,
                canAccessSearchModule,
                canEditPatient,
                canEditSurgery,
                canEditImplant,
                canEditImplantWarranty,
                canEditImplantTrackingStatus,
                canEditUSApproval,

                canCreateUSApproval,

                canAccessReportModule,
                canAccessCustomerCareReport,
                canAccessDistributorReport,
                canAccessImplantReport,
                canAccessDeviceTrackingPMS,
                canAccessExplantInvestigationPMS,

                canAccessUserModule,
                canAddUser,
                canEditUser,
                canViewUserActivityLog,

                canAccessSettingModule,
                canAccessUSASupportFeatures,
                canAccessRebateProgram,
                canAccessMaintenanceModule,
            }}
        >
            {children}
        </UserContext.Provider>
    );
};
